/* ─── Plain-English rule logic formatter ───────────────────────────────────
   Turns a rule's conditions_tree + actions into layman-readable text,
   recursing into nested AND/OR groups (unlike RuleCard's flat sentence
   builder). Falls back gracefully to humanized keys when registry data
   (fact_registry / action_registry, fetched from /rules/facts and
   /rules/actions) doesn't cover a key.                                    */

// Same wording as RuleCard.jsx's OP_LABELS, kept consistent across the app.
const OP_LABELS = {
  ">=": "is at least",
  ">":  "is more than",
  "<=": "is at most",
  "<":  "is less than",
  "==": "equals",
  "!=": "is not",
};

const PARAM_LABELS = {
  quantity_from:       "quantity from",
  roll_to_next_expiry: "roll to next expiry",
  safe_dist_pct:       "safe distance %",
  reason:              "reason",
  min_net_profit:      "minimum net profit required",
};

export const humanizeKey = (key) =>
  (key || "")
    .toString()
    .toLowerCase()
    .split("_")
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

const isNumericString = (s) => typeof s === "string" && /^-?\d+(\.\d+)?$/.test(s);

export const formatValue = (value) => {
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return value.toLocaleString();
  if (typeof value === "string") {
    if (value.toLowerCase() === "true") return "Yes";
    if (value.toLowerCase() === "false") return "No";
    if (isNumericString(value)) return Number(value).toLocaleString();
    return humanizeKey(value);
  }
  return String(value);
};

export const factLabel = (factKey, factsByKey) =>
  (factsByKey && factsByKey[factKey]?.label) || humanizeKey(factKey);

export const actionLabel = (action, actionsByKey) => {
  const base = (actionsByKey && actionsByKey[action.action_key]?.label) || humanizeKey(action.action_key);
  const extras = Object.entries(action).filter(([k]) => k !== "action_key");
  if (extras.length === 0) return base;
  const parts = extras.map(([k, v]) => `${PARAM_LABELS[k] || humanizeKey(k)}: ${formatValue(v)}`);
  return `${base} (${parts.join(", ")})`;
};

const formatExpression = (expr, factsByKey) => {
  if (!expr) return "";
  return expr.replace(/[A-Za-z_][A-Za-z0-9_]*/g, (token) =>
    (factsByKey && factsByKey[token]?.label) || token
  );
};

export const describeLeaf = (node, factsByKey) => {
  const subject = factLabel(node.fact, factsByKey);
  const opText = OP_LABELS[node.operator] || node.operator;

  if (node.value_type === "EXPRESSION" || node.expression) {
    return `${subject} ${opText} ${formatExpression(node.expression, factsByKey)}`;
  }
  if (node.value_type === "DYNAMIC" || node.value_from) {
    return `${subject} ${opText} your configured "${factLabel(node.value_from, factsByKey)}" setting`;
  }
  return `${subject} ${opText} ${formatValue(node.value)}`;
};

export const isGroupNode = (node) =>
  !!node && (node.type === "AND" || node.type === "OR") && Array.isArray(node.children);

export const groupLabel = (node) =>
  node.type === "AND" ? "All of the following are true:" : "At least one of the following is true:";
